<script setup lang="ts">
import { computed, toRef, watch } from '#imports';
import { useScrollLock } from '@vueuse/core';

import IconCrossBold from '@/assets/icons/icon-cross-bold.svg';

const props = defineProps<{
	modelValue: boolean;
}>();

const emit = defineEmits<{
	(e: 'update:modelValue', val: boolean): void;
}>();

const isOpen = computed({
	get: () => props.modelValue,
	set: (value: boolean) => emit('update:modelValue', value),
});

const isOpenRef = toRef(props, 'modelValue');
const $html = document?.documentElement;
const isLocked = useScrollLock($html);

watch(isOpenRef, () => {
	isLocked.value = isOpen.value;
}, { immediate: true });
</script>

<template>
	<teleport to="#dialog">
		<transition name="fade" appear>
			<div
				v-if="isOpen"
				class="dialog-overlay"
				@mousedown.self="isOpen	= false"
			>
				<div class="dialog">
					<button class="dialog__close button button--icon" @click="isOpen = false">
						<icon-cross-bold class="dialog__close-icon icon" />
					</button>

					<v-scroll class="dialog__content">
						<slot />
					</v-scroll>
				</div>
			</div>
		</transition>
	</teleport>
</template>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity $transition-duration ease-in-out;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.dialog {
	position: relative;

	width: 100%;
	max-width: rem(448px);

	border-radius: rem($radius-primary);
	background-color: $white;

	&-overlay {
		@include position-stretching(fixed, $z-index-popup);

    display: flex;
    align-items: center;
    justify-content: center;

    transition: opacity $transition-duration ease-in-out;

		background: rgba($black, .6);
	}

	&__close {
		$gap: 4px;
		$size: 30px;

		position: absolute;
		z-index: 1;
		top: rem($gap);
		right: rem($gap);

		width: rem($size);
		height: rem($size);

		color: $black;
	}

	&__content {
		max-height: 90dvh;
		padding: rem($gap-medium);

		@supports not (max-height: 90dvh) {
			max-height: 90dvh;
		}
	}
}
</style>
